import type {Analytics} from 'firebase/analytics';
import type {FirebaseOptions} from 'firebase/app';
import {forwardToGoogleAds, forwardToGtm, forwardToMetaPixel} from './destinations';
import type {AnalyticsEventMap} from './events';

export interface AnalyticsConfig {
  apiKey: string;
  projectId: string;
  appId: string;
  measurementId: string;
  debug?: boolean;
}

// Supplied at RUNTIME by the server layout (see readAnalyticsConfig), not read from
// process.env here: the deployed values arrive as plain FIREBASE_* container env, which only
// server code can see. Reading them here would require the NEXT_PUBLIC_ prefix, and those are
// frozen into the bundle at `next build` — long before the task definition exists.
let config: AnalyticsConfig | null = null;

export function setAnalyticsConfig(next: AnalyticsConfig | null): void {
  if (config || !next) {
    return;
  }
  config = next;
}

export function isAnalyticsConfigured(): boolean {
  return Boolean(config?.apiKey && config.projectId && config.appId && config.measurementId);
}

function buildFirebaseOptions(config: AnalyticsConfig): FirebaseOptions {
  return {
    apiKey: config.apiKey,
    projectId: config.projectId,
    appId: config.appId,
    measurementId: config.measurementId,
  };
}

interface FirebaseAnalyticsSdk {
  analytics: Analytics;
  logEvent: typeof import('firebase/analytics').logEvent;
}

let instancePromise: Promise<FirebaseAnalyticsSdk | null> | null = null;
let warnedDisabled = false;

const IDLE_TIMEOUT_MS = 3000;

// gtag.js is ~150 KiB and competes with hydration; events queue on the promise, so
// nothing is lost by waiting for the page to finish loading and go idle first.
function whenPageIdle(): Promise<void> {
  return new Promise(resolve => {
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(
          () => {
            resolve();
          },
          {timeout: IDLE_TIMEOUT_MS},
        );
      } else {
        setTimeout(resolve, 0);
      }
    };
    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, {once: true});
    }
  });
}

async function loadFirebaseAnalytics(active: AnalyticsConfig): Promise<FirebaseAnalyticsSdk | null> {
  const [{initializeAnalytics, isSupported, logEvent}, {getApp, getApps, initializeApp}] = await Promise.all([
    import('firebase/analytics'),
    import('firebase/app'),
  ]);
  if (!(await isSupported())) {
    return null;
  }
  const app = getApps().length > 0 ? getApp() : initializeApp(buildFirebaseOptions(active));
  // GA4's automatic first page_view is disabled so every page view flows
  // through AnalyticsPageViews — otherwise the landing page double-counts.
  const analytics = initializeAnalytics(app, {
    config: {send_page_view: false, ...(active.debug ? {debug_mode: true} : {})},
  });
  return {analytics, logEvent};
}

function getAnalyticsSdk(): Promise<FirebaseAnalyticsSdk | null> {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (!config || !isAnalyticsConfigured()) {
    if (!warnedDisabled && process.env.NODE_ENV !== 'production') {
      console.warn('[analytics] disabled — FIREBASE_* config is missing');
      warnedDisabled = true;
    }
    return Promise.resolve(null);
  }
  const active = config;
  instancePromise ??= whenPageIdle()
    .then(() => loadFirebaseAnalytics(active))
    .catch(() => null);
  return instancePromise;
}

export function getAnalyticsInstance(): Promise<Analytics | null> {
  return getAnalyticsSdk().then(sdk => sdk?.analytics ?? null);
}

export function logAnalyticsEvent<K extends keyof AnalyticsEventMap>(name: K, params: AnalyticsEventMap[K]): void {
  // Optional catalog params arrive as `undefined` when omitted — strip them so
  // GA4 never receives literal "undefined" values.
  const cleanedParams = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined));
  forwardToGtm(name, cleanedParams);
  forwardToMetaPixel(name, cleanedParams);
  forwardToGoogleAds(name);
  void getAnalyticsSdk()
    .then(sdk => {
      // The string cast selects firebase's custom-event overload (the typed
      // overloads only cover GA4's built-in event names).
      if (sdk) {
        sdk.logEvent(sdk.analytics, name as string, cleanedParams);
      }
    })
    .catch(() => undefined);
}
